import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ReinstatementReason, StartTerminationPayload, TemplateTask, agirNaTarefa, cancelarDesligamento, cancelarReintegracao,
  concluirDesligamento, decidirReintegracao, desligamentosDaPessoa, iniciarDesligamento, listarModelos,
  listarMotivosReintegracao, minhasTarefas, reenviarReintegracao, reintegracoesDaPessoa, reintegracoesPendentes, salvarModelo,
  salvarMotivoReintegracao, situacaoModelo, solicitarReintegracao,
} from '../api/desligamento';
import { PESSOAS_KEYS } from './index';

export const DS_KEYS = {
  modelos: (all: boolean) => ['pessoas', 'desligamento', 'modelos', all] as const,
  pessoa: (id: string) => ['pessoas', 'desligamento', id] as const,
  minhas: ['pessoas', 'desligamento', 'minhas'] as const,
  motivos: (all: boolean) => ['pessoas', 'reintegracao', 'motivos', all] as const,
  reintegracoes: (id: string) => ['pessoas', 'reintegracao', id] as const,
  pendentes: ['pessoas', 'reintegracao', 'pendentes'] as const,
};

function useWrite<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: PESSOAS_KEYS.all }) });
}

export const useModelosDesligamento = (all = false, enabled = true) =>
  useQuery({ queryKey: DS_KEYS.modelos(all), queryFn: () => listarModelos(all), enabled });
export const useSalvarModelo = () =>
  useWrite(({ id, payload }: { id: string | null; payload: { name: string; employmentType: string; tasks: TemplateTask[] } }) =>
    salvarModelo(id, payload));
export const useSituacaoModelo = () => useWrite(({ id, active }: { id: string; active: boolean }) => situacaoModelo(id, active));

export const useDesligamentos = (personId: string, enabled = true) =>
  useQuery({ queryKey: DS_KEYS.pessoa(personId), queryFn: () => desligamentosDaPessoa(personId), enabled });
export const useIniciarDesligamento = (bondId: string) =>
  useWrite((payload: StartTerminationPayload) => iniciarDesligamento(bondId, payload));
export const useConcluirDesligamento = () => useWrite((id: string) => concluirDesligamento(id));
export const useCancelarDesligamento = () =>
  useWrite(({ id, justification }: { id: string; justification: string }) => cancelarDesligamento(id, justification));
export const useAgirNaTarefa = () =>
  useWrite(({ id, action, notes }: { id: string; action: 'concluir' | 'dispensar'; notes?: string }) => agirNaTarefa(id, action, notes));
export const useMinhasTarefas = () => useQuery({ queryKey: DS_KEYS.minhas, queryFn: minhasTarefas });

export const useMotivosReintegracao = (all = false, enabled = true) =>
  useQuery({ queryKey: DS_KEYS.motivos(all), queryFn: () => listarMotivosReintegracao(all), enabled });
export const useSalvarMotivoReintegracao = () =>
  useWrite(({ id, payload }: { id: string | null; payload: Partial<ReinstatementReason> }) => salvarMotivoReintegracao(id, payload));
export const useReintegracoes = (personId: string, enabled = true) =>
  useQuery({ queryKey: DS_KEYS.reintegracoes(personId), queryFn: () => reintegracoesDaPessoa(personId), enabled });
export const useSolicitarReintegracao = (bondId: string) =>
  useWrite((payload: { reasonId: string; justification: string; reinstatementDate: string; documentId?: string }) =>
    solicitarReintegracao(bondId, payload));
export const useReintegracoesPendentes = () => useQuery({ queryKey: DS_KEYS.pendentes, queryFn: reintegracoesPendentes });
export const useDecidirReintegracao = () =>
  useWrite(({ id, decision, comment }: { id: string; decision: string; comment?: string }) => decidirReintegracao(id, decision, comment));
export const useReenviarReintegracao = () =>
  useWrite(({ id, justification, documentId }: { id: string; justification: string; documentId?: string }) =>
    reenviarReintegracao(id, justification, documentId));
export const useCancelarReintegracao = () =>
  useWrite(({ id, justification }: { id: string; justification: string }) => cancelarReintegracao(id, justification));
